import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import React, { useState, useMemo } from "react";
import { MdArrowDropDown } from "react-icons/md";
import { LightText2 } from "../ui/Text";

interface Option {
  label: string;
  value: string | number;
}

interface RadixSelectProps {
  options: Option[];
  placeholder?: string;
  onChange: (value: Option) => void;
  selectedValue: Option;
  className?: string;
  dropdownClassName?: string;
  listClassName?: string;
  count?: number;
  showSearch?: boolean;
  activeClass?: string;
}

const RadixSingleSelect: React.FC<RadixSelectProps> = ({
  options,
  placeholder,
  onChange,
  selectedValue,
  className = "",
  dropdownClassName = "",
  listClassName = "",
  count,
  showSearch = false,
  activeClass = "font-black",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const s = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(s));
  }, [search, options]);

  const handleSelectOption = (value: Option) => {
    onChange(value);
    setSearch("");
    setIsOpen(false);
  };

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger asChild>
        <button className={`rounded-md flex items-center ${className}`} onClick={(e) => e.preventDefault()}>
          <span>{selectedValue?.label || placeholder}</span>
          {count ? <span className="px-0.5">{` (${count})`}</span> : null}
          <MdArrowDropDown className={`size-5 text-icon-default2 ${isOpen ? "rotate-180" : ""}`} />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content sideOffset={4} className={`z-50 w-fit bg-white border rounded-md shadow-lg px-3 py-2 whitespace-nowrap ${dropdownClassName}`}>
          {showSearch && (
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Search..."
              className="px-3 py-2 mb-2 rounded border border-fieldBorder focus:border-text-primary text-13 outline-none text-text-default w-full"
            />
          )}
          <ul className={`flex flex-col ${listClassName}`}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <DropdownMenu.Item
                  key={option.value}
                  className={`px-2 py-2 cursor-pointer hover:bg-primaryOpacity-10 hover:text-text-primary text-13 ${
                    option.value === selectedValue?.value ? activeClass : ""
                  }`}
                  onSelect={() => handleSelectOption(option)}
                >
                  {option.label}
                </DropdownMenu.Item>
              ))
            ) : (
              <li className="px-4 py-6">
                <LightText2 text="No options found" />
              </li>
            )}
          </ul>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default RadixSingleSelect;
